import { RiskbarComponent } from './riskbox/riskbar.component';
import { RiskboxComponent } from './riskbox/riskbox.component';

import { AuthService } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import {
  EgpMatrix,
  EgpRegion,
  EgpRegionAndCountry,
  EgpRegions,
  EgpResponse,
  EgpSeason,
  EgpService,
} from './egp.service';

import { DatexPipe } from '../shared/datex.pipe';
import { KeyPipe } from '../shared/key.pipe';
import { ShowMoreDaysPipe } from './pipes/show-more-days.pipe';
import { ShowRiskboxPipe } from './pipes/show-riskbox.pipe';
import { TitlePipe } from './pipes/title.pipe';
import { WDayPipe } from './pipes/w-day.pipe';

import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, ViewportScroller } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { TippyDirective } from '@ngneat/helipopper';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

export const MODULES = [
  CommonModule,
  MatFormFieldModule,
  MatSelectModule,
  ReactiveFormsModule,
  MatCardModule,
  MatIconModule,
  MatButtonModule,
  TranslateModule,
];
export const COMPONENTS = [RiskboxComponent, FaIconComponent, RiskbarComponent];
export const PIPES = [
  ShowRiskboxPipe,
  ShowMoreDaysPipe,
  DatexPipe,
  KeyPipe,
  WDayPipe,
  TitlePipe,
];
export const DIRECTIVES = [TippyDirective];

@Component({
  selector: 'app-egp',
  templateUrl: './egp.component.html',
  styleUrls: ['./egp.component.scss'],
  providers: [EgpService],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES, MatTooltip],
})
export class EgpComponent implements OnInit {
  public egpService = inject(EgpService);
  public auth = inject(AuthService);
  private translate = inject(TranslateService);
  private breakpointObserver = inject(BreakpointObserver);
  private viewportScroller = inject(ViewportScroller);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public languageService = inject(LanguageService);

  tabsControl = new UntypedFormControl();
  regionControl = new UntypedFormControl();
  selectedRegion = signal<EgpRegionAndCountry>(undefined);
  isLoading = true;
  seasons: WritableSignal<Array<EgpSeason>> = signal([]);
  egpRegions: WritableSignal<EgpRegions> = signal({
    summer: [],
    winter: [],
  });

  hasEgpProducts = {
    summer: 0,
    winter: 0,
  };

  allEgpData = {
    summer: {},
    winter: {},
  };
  previousRiskboxData = {};
  isRiskboxDifferent = signal(false);
  egpData: WritableSignal<EgpResponse> = signal(undefined);

  translations = {};
  sub: Subscription;

  language = 'de';

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe('(max-width: 1000px)')
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  static getOrderedSeasons(): Array<EgpSeason> {
    const currentMonth = moment().month();
    if (currentMonth > 2 && currentMonth < 9) {
      return [EgpSeason.SUMMER, EgpSeason.WINTER];
    }
    return [EgpSeason.WINTER, EgpSeason.SUMMER];
  }

  ngOnInit() {
    this.getTranslations();
    this.initEgpRegions();
  }

  async initEgpRegions(): Promise<void> {
    const egpProducts = {
      summer: [],
      winter: [],
    };
    this.seasons.set([...EgpComponent.getOrderedSeasons()]);
    for (const season of this.seasons()) {
      const customerDocument = this.auth.getCustomerDocument();
      const productType =
        season === EgpSeason.WINTER ? 'egpProducts' : 'egpSummerProducts';
      const allCustomersEgwProducts = customerDocument[productType];
      egpProducts[season] = allCustomersEgwProducts.filter(product =>
        this.auth.isProductActive(productType, product.id)
      );
      this.hasEgpProducts[season] = egpProducts[season].length;
      await this.extractEgpRegions(season, egpProducts[season]);
    }
    this.isLoading = false;
  }

  async extractEgpRegions(
    season4Region: EgpSeason,
    egpProducts: Array<any>
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.egpService
        .getAllEgpAreas(season4Region, true)
        .then(async allRegions => {
          const validRegions: Array<EgpRegionAndCountry> = [];
          for (const product of egpProducts) {
            for (const region of allRegions) {
              if (region.id === product['areaId'] + '') {
                const validRegion = {
                  id: region.id,
                  name: region.name,
                  season: season4Region,
                  country: region.superArea['country'],
                } as EgpRegionAndCountry;
                if (
                  !validRegions.some(
                    r =>
                      r.id === validRegion.id && r.season === validRegion.season
                  )
                ) {
                  validRegions.push(validRegion);
                  await this.fetchEgpData(validRegion);
                }
              }
            }
          }
          this.egpRegions.update(egpRegions => {
            egpRegions[season4Region] = validRegions.sort((a, b) =>
              a.name > b.name ? 1 : -1
            );
            return { ...egpRegions };
          });
          const egpRegions = this.egpRegions();
          this.sub = this.route.params.subscribe(params => {
            if (params && params.hasOwnProperty('region')) {
              const region = params['region'];
              const [season, regionId] = region.split('_');
              this.setEgpRegionId(season, regionId);
            } else if (this.selectedRegion()) {
              this.router.navigate([
                '/egp',
                this.selectedRegion().season + '_' + this.selectedRegion().id,
              ]);
            } else if (egpRegions[season4Region].length > 0) {
              const egpRegion = egpRegions[season4Region][0];
              this.router.navigate([
                '/egp',
                egpRegion.season + '_' + egpRegion.id,
              ]);
            }
          });
          /*
          if (!this.selectedRegion() && this.egpRegions()[season4Region].length > 0) {
            const egpRegion = this.egpRegions()[season4Region][0];
            this.router.navigate(['/egp', egpRegion.season + '_' + egpRegion.id]);
//            this.setEgpRegion(this.egpRegions()[season4Region][0]);
          }

           */
          resolve();
        });
    });
  }

  fetchEgpData(region: EgpRegion): Promise<void> {
    return new Promise((resolve, reject) => {
      this.egpService
        .getEgp(region.id)
        .then((currentAndPreviousEgp: Array<EgpResponse>) => {
          this.onlyTakeNoOutdatedEgp(currentAndPreviousEgp, region);
          resolve();
        })
        .catch(err => {
          console.log('Error: No egp for ', region);
          this.allEgpData[region.season][region.id] = {};
          this.previousRiskboxData[region.id] = {};
          resolve();
        });
    });
  }

  onlyTakeNoOutdatedEgp(
    currentAndPreviousEgp: Array<EgpResponse>,
    region: EgpRegion
  ): void {
    const dateOfEgp = moment(currentAndPreviousEgp[0].textObject.prescribeAt);
    const today = moment();
    const daysWhenEgpIsTooOld = 30;

    if (today.diff(dateOfEgp, 'days') >= daysWhenEgpIsTooOld) {
      this.allEgpData[region.season][region.id] = {};
      this.previousRiskboxData[region.id] = {};
    } else {
      this.allEgpData[region.season][region.id] = currentAndPreviousEgp[0];
      this.previousRiskboxData[region.id] =
        currentAndPreviousEgp && currentAndPreviousEgp[1]
          ? currentAndPreviousEgp[1].matrices[0]
          : null;
    }
  }

  setEgpRegionId(season: string, regionId: string): void {
    const selectedRegion = this.egpRegions()[season].find(
      (r: EgpRegionAndCountry) => r.id === regionId
    );
    if (selectedRegion) {
      this.setEgpRegion(selectedRegion);
    }
  }

  setEgpRegion(egpRegion: EgpRegionAndCountry): void {
    this.selectedRegion.set({ ...egpRegion });
    this.regionControl.setValue(
      this.selectedRegion().season + this.selectedRegion().id
    );
    this.egpData.set({ ...this.allEgpData[egpRegion.season][egpRegion.id] });

    if (Object.keys(this.egpData()).length > 0) {
      this.isRiskboxDifferent.set(
        this.wasRiskboxValuesChanged(
          this.egpData().matrices[0],
          this.previousRiskboxData[egpRegion.id]
        )
      );
    }
  }

  onRegionChange(season: string, event: MatOptionSelectionChange): void {
    if (event.isUserInput) {
      const regionId = event.source.value.replace(season, '');
      this.router.navigate(['/egp', season + '_' + regionId]);
      //      this.setEgpRegionId(season, regionId);
      //      const selectedRegion = this.egpRegions()[season].filter((r: EgpRegion) => r.id === regionId)[0];
      //      this.setEgpRegion(selectedRegion);
    }
  }

  getTranslations(): void {
    this.language = localStorage.getItem('language');
    this.translations = this.translate.instant('PRODUCT.EGP');
  }

  wasRiskboxValuesChanged(
    currentRiskbox: EgpMatrix,
    previousRiskbox: EgpMatrix
  ): boolean {
    if (!previousRiskbox) return false;
    for (const slipperyType of Object.keys(currentRiskbox)) {
      if (
        currentRiskbox[slipperyType].regionPercent !==
        previousRiskbox[slipperyType].regionPercent
      ) {
        return true;
      }
      if (
        currentRiskbox[slipperyType].riskPercent !==
        previousRiskbox[slipperyType].riskPercent
      ) {
        return true;
      }
    }
    return false;
  }

  public onClickScroll(elementId: string): void {
    this.viewportScroller.scrollToAnchor(elementId);
  }

  public onClickScrollToStart(): void {
    this.viewportScroller.scrollToPosition([0, -100]);
  }
}
